<script>
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { fly, fade, slide } from 'svelte/transition';

    import { config } from '../config.js';
    import { vptServer, token, userName, staffUrn } from '../stores.js';
    import { formatDate, formatDateTime, getDaysDifference, getIsoDateStringAsDate } from '../date-time.js';

    import ContactForm from './ContactForm.svelte';

    const dispatch = createEventDispatcher();

    export let inmateUrn;
    export let inmateName;
    export let headerText = "";
    export let canAddContact = true;

    export let contactId;
    export let contactDateTime;
    export let previousNotes;
    export let staff;
    export let dueDate;
    export let previousAction;

    export let overdueDays;
    export let targetedTotalIncreased;
    export let isolationIncreased;

    let image;
    let displayAddContactForm = false;
    let contactDate;
    let contactTime;
    let contactNotes;
    let seenBy;
    let followUp = false;
    let followUpDate;
    let followUpAction;
    let isFormValid;
    let showValidation = false;

    // Prevent double click when saving
    let isSaving = false;

    $: warningCount = 0 + (overdueDays > 0 ? 1 : 0) + (targetedTotalIncreased ? 1 : 0) + (isolationIncreased ? 1 : 0);
        
    // Warnings are generated based on the conditions provided and conditionally added to the warnings array.
    $: warnings = [
        ... (overdueDays > 0) ? [`Follow-up overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'}.`] : [],
        ... (targetedTotalIncreased) ? ['Targeted total has increased since the last contact.'] : [],
        ... (isolationIncreased) ? ['Isolation has increased since the last contact.'] : []
    ];

    $: difference = dueDate ? getDaysDifference(getIsoDateStringAsDate(dueDate), new Date()) : null;

    onMount(() => {
        seenBy = $staffUrn;
        getPrimaryImage();
    });

    async function getPrimaryImage() {
        await fetch(`${$vptServer}/inmate/${inmateUrn}/image`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            if (data.image) {
                image = data.image;
            }
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }

     function add() {
        isSaving = true;
        if (isFormValid) {
            addContact();
        } else {
            showValidation = true;
            isSaving = false;
        }
    }

    async function addContact() {
        await fetch(`${$vptServer}/inmate/${inmateUrn}/contact/insert`, {
            headers: {
                "Authorization": "Bearer " + $token,
                "Content-Type": "application/json"
            },
            method: 'POST',
            body: JSON.stringify({
                previousContactId: contactId,
                typeId: $config.contactTypeId,
                contactDateTime: contactDate + " " + contactTime,
                notes: contactNotes,
                actionDueDate: followUp ? followUpDate : undefined,
                actionNotes: followUp ? followUpAction : undefined,
                userId: $userName,
                staffId: seenBy
            })
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            dispatch('close', {refresh: true});
            return r;
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }
    
    function closeModal() {
        dispatch('close', {refresh: false});
    }
</script>

<style>
    #staff-details, #follow-up-details {
        margin-bottom: 5px;
        font-size: 11px;
        color: #676a6d
    }

    #follow-up-details {
        margin-top: 18px;
    }

    #follow-up-details span:first-of-type {
        margin-right: 3px;
    }

    .overdue-icon {
        color: #bb2e3b;
    }

    #add-contact-button {
        display: flex;
        align-items: center;
        color: #0267B3;
    }

    #add-contact-button span:first-of-type {
        margin-right: 5px;
    }

    #add-contact-form {
        border-top: 1px solid #dee2e6;
    }
</style>

<div class="modal" tabindex="-1" role="dialog" aria-label="Contact">
    <div in:fly={{ y: -100 }} class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header" class:img={image && !headerText}>
                {#if headerText}
                    <h5 class="modal-title">{headerText}</h5>
                {:else}
                    {#if image}
                        <img in:fade={{ duration: 300 }} id="inmate-image" src="data:image/jpg;base64,{image}" alt="inmate" />
                    {/if}
                    <div class="modal-title">
                        <h5>Contact</h5>
                        <span>{inmateName.toUpperCase()}</span>
                    </div>
                {/if}
                <button type="button" class="close" aria-label="Close" on:click={closeModal}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>

            {#if warningCount > 0}
                <div class="modal-alert modal-alert-header modal-alert-danger">
                    <span class="icon"><i class="bi bi-exclamation-triangle" /></span>
                    {#if warningCount > 1}
                        <ul>
                            {#each warnings as warning}
                                <li>{warning}</li>
                            {/each}
                        </ul>
                    {:else}
                        <span>{warnings[0]}</span>
                    {/if}
                </div>
            {/if}
                
            <div class="modal-body">
                <div id="staff-details">
                    <span>Seen by {staff} on {formatDateTime(contactDateTime)}</span>
                </div>
                
                <div><span>{previousNotes}</span></div>

                {#if dueDate}
                    <div id="follow-up-details">
                        <span class:overdue-icon={overdueDays > 0}>
                            <i class="bi bi-clock{(overdueDays > 0) ? '-fill' : ''}" /> 
                        </span>
                        <span>
                            {#if overdueDays > 0}
                                Follow-up was due on {formatDate(dueDate)}
                            {:else}
                                Follow-up is due 
                                {#if difference == 0}
                                    today
                                {:else if difference == 1}
                                   tomorrow
                                {:else}
                                    in {difference} days ({formatDate(dueDate)})
                                {/if}
                            {/if}
                        </span>
                    </div>

                    <div>
                        {#if previousAction}
                            <span>{previousAction}</span>
                        {:else}
                            <span class="text-muted">No follow-up notes were recorded.</span>
                        {/if}
                    </div>
                {/if}
            </div>
            
            {#if canAddContact}
                <div id="add-contact-form" class="modal-body">
                    {#if !displayAddContactForm}
                        <div out:slide|local={{ duration : 500 }}>
                                <button id="add-contact-button" class="button-reset focus-outline" type="button" 
                                        on:click={() => displayAddContactForm = true}>
                                    <span><i class="bi bi-plus-lg" /></span>
                                    <span>Add Another Contact</span>
                                </button>
                            </div>
                    {:else}
                        <div in:slide|local={{ duration : 500 }}>
                            <ContactForm bind:contactDate bind:contactTime bind:contactNotes bind:seenBy
                                bind:followUp bind:followUpDate bind:followUpAction bind:isFormValid bind:showValidation 
                                previousFollowUpStaff={dueDate ? staff : null} />
                        </div>
                    {/if}
                </div>

                {#if displayAddContactForm && followUp}
                    <div transition:slide|local={{ duration : 500 }} class="modal-alert modal-alert-footer">
                        <span class="icon"><i class="bi bi-info-circle" /></span>
                        <span>Consider the ACCT process if the {$config.inmateLabelName} is at risk.</span>
                    </div>
                {/if}
            {/if}

            <div class="modal-footer">
                {#if !displayAddContactForm}
                    <button type="button" class="btn btn-primary" on:click={closeModal}>Close</button>
                {:else}
                    <button type="button" class="btn btn-primary" disabled={isSaving} 
                        on:click={() => add()}>Add Contact</button>
                {/if}
            </div>
        </div>
    </div>
</div>

<div class="modal-backdrop show"></div>
